import { useRef, useState, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@legendapp/state/react';
import { auth$ } from '@/stores/auth$';
import { ui$, setViewMode, setSelectedTagFilter, setActiveNoteId, toggleMobileSidebar } from '@/stores/ui$';
import { createNote } from '@/stores/notes$';
import { Menu, Plus, User, LogOut, X, Plug, Settings } from 'lucide-react';
import { logout } from '@/stores/auth$';

// Lazy load modals
const MCPModal = lazy(() => import('./MCPModal'));
const SettingsModal = lazy(() => import('./SettingsModal'));

const MobileTopBar = observer(function MobileTopBar() {
  const user = auth$.user.get();
  const theme = ui$.theme.get();
  const isMobileSidebarOpen = ui$.isMobileSidebarOpen.get();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMCPModal, setShowMCPModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleAddNote = () => {
    if (user) {
      createNote('Untitled Note', user.id, (noteId) => setActiveNoteId(noteId));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle ?action= deeplinks
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action === 'mcp-access') {
      setShowMCPModal(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (showUserMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
        width: 200,
      });
    }
  }, [showUserMenu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e) => {
      // Check if click is outside both button and dropdown
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  return (
    <div className="md:hidden fixed top-0 left-0 right-0 h-[var(--mobile-header-h)] bg-background/95 backdrop-blur-sm border-b border-border z-app-header flex items-center justify-between px-4 pt-[var(--sat)]">
      {/* Left: Hamburger / Close */}
      <button
        onClick={() => toggleMobileSidebar()}
        className="p-2 -ml-2 text-foreground hover:bg-accent rounded-md transition-colors"
        aria-label={isMobileSidebarOpen ? "Close menu" : "Open menu"}
      >
        {isMobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Center: Logo */}
      <h1
        onClick={() => {
          setViewMode('notes');
          setSelectedTagFilter(null);
          setActiveNoteId(null);
        }}
        className="font-bold text-lg tracking-tight cursor-pointer hover:opacity-80 transition-opacity select-none"
      >
        Prompt<span className="text-primary">Note</span>
      </h1>

      {/* Right: New Note + User */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleAddNote}
          className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
          aria-label="New note"
        >
          <Plus size={20} />
        </button>

        <button
          ref={buttonRef}
          onClick={() => setShowUserMenu(!showUserMenu)}
          className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors"
          aria-label="User menu"
        >
          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
            <User size={14} />
          </div>
        </button>

        {/* User Menu Dropdown */}
        {showUserMenu && createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-popover border border-border rounded-lg shadow-lg py-1 z-dropdown animate-in fade-in slide-in-from-top-2 duration-100"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {/* User Info */}
            <div className="px-3 py-2 border-b border-border/50">
              <p className="text-xs font-medium truncate">{user?.username || user?.email || 'User'}</p>
              <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
            </div>

            {/* Theme Options - Placeholder for now */}
            <div className="px-3 py-2 text-xs text-muted-foreground">Theme: {theme}</div>

            {/* Actions */}
            <button
              onClick={() => {
                setShowSettingsModal(true);
                setShowUserMenu(false);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
            >
              <Settings size={14} />
              <span>Settings</span>
            </button>
            <button
              onClick={() => {
                setShowMCPModal(true);
                setShowUserMenu(false);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
            >
              <Plug size={14} />
              <span>MCP / API Access</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
            >
              <LogOut size={14} />
              <span>Log out</span>
            </button>
          </div>,
          document.body
        )}
      </div>

      {/* Lazy-loaded modals */}
      <Suspense fallback={null}>
        {showMCPModal && (
          <MCPModal
            isOpen={showMCPModal}
            onClose={() => setShowMCPModal(false)}
          />
        )}

        {showSettingsModal && (
          <SettingsModal
            isOpen={showSettingsModal}
            onClose={() => setShowSettingsModal(false)}
          />
        )}
      </Suspense>
    </div>
  );
});

export default MobileTopBar;

